import React, {useState, useEffect} from 'react';
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  getDocs,
  query,
  where,
  writeBatch,
  increment,
  runTransaction,
  setDoc,
} from 'firebase/firestore';
import {db} from '../firebase';
import {format, startOfDay, endOfDay} from 'date-fns';
import type {TimeSlot, StarAvailabilitySettings, Booking} from '../types/availability';
import {useAuth} from '../contexts/AuthContext';
import Calendar from 'react-calendar';
import type {Value} from 'react-calendar/dist/cjs/shared/types';
import 'react-calendar/dist/Calendar.css';
import './BookingModal.css';
import { createNotification } from '../services/notificationService';
import { sendBookingConfirmationEmail } from '../services/emailService';
import PaymentForm from './PaymentForm';
import { toast } from 'react-hot-toast';
import TimeSlotPicker from './TimeSlotPicker';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

interface BookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  starId: string;
}

const BookingModal: React.FC<BookingModalProps> = ({
  isOpen,
  onClose,
  starId,
}) => {
  const {user, setUser} = useAuth();
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  const [selectedStartTime, setSelectedStartTime] = useState<Date | null>(null);
  const [selectedEndTime, setSelectedEndTime] = useState<Date | null>(null);
  const [bookingDuration, setBookingDuration] = useState<number>(10);
  const [durationInput, setDurationInput] = useState<string>('10');
  const [isLoadingSlots, setIsLoadingSlots] = useState<boolean>(false);
  const [isProcessingBooking, setIsProcessingBooking] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [bidAmount, setBidAmount] = useState<number>(0);
  const [bidInput, setBidInput] = useState<string>('0');
  const [slots, setSlots] = useState<TimeSlot[]>([]);
  const [settings, setSettings] = useState<StarAvailabilitySettings>({
    minCallDuration: 5,
    maxCallDuration: 30,
    pricePerMinute: 1,
    minBidIncrement: 5,
    allowBidding: true,
  });
  const [affectedBookings, setAffectedBookings] = useState<Booking[]>([]);
  const [minBid, setMinBid] = useState<number>(0);
  const [timeValue, setTimeValue] = useState("12:00");
  const [emailOnOutbid, setEmailOnOutbid] = useState<boolean>(true);
  const [daySlots, setDaySlots] = useState<TimeSlot[]>([]);

  // Helper: checks if a calendar date falls within a slot's time span
  // A slot from 10 PM Mar 20 to 3 AM Mar 21 should appear on both Mar 20 and Mar 21
  const slotSpansDate = (slot: TimeSlot, date: Date): boolean => {
    const dateStart = startOfDay(date).getTime();
    const dateEnd = endOfDay(date).getTime();
    // Slot overlaps this calendar day if it starts before the day ends AND ends after the day starts
    return slot.startTime.getTime() <= dateEnd && slot.endTime.getTime() >= dateStart;
  };

  // Helper: construct a proper Date from selectedDate + time, handling cross-midnight slots
  const buildBookingStart = (hours: number, minutes: number, slot: TimeSlot): Date => {
    // Try the selected date first
    const candidate = new Date(selectedDate);
    candidate.setHours(hours, minutes, 0, 0);

    // If candidate falls within the slot, use it
    if (candidate >= slot.startTime && candidate < slot.endTime) {
      return candidate;
    }

    // If the slot crosses midnight and the time is in the next-day portion,
    // the candidate date needs to be bumped by 1 day
    const nextDay = new Date(selectedDate);
    nextDay.setDate(nextDay.getDate() + 1);
    nextDay.setHours(hours, minutes, 0, 0);
    if (nextDay >= slot.startTime && nextDay < slot.endTime) {
      return nextDay;
    }

    // If the slot crosses midnight and the time is in the prev-day portion,
    // (fan clicked end date but picked a time from the start-date side)
    const prevDay = new Date(selectedDate);
    prevDay.setDate(prevDay.getDate() - 1);
    prevDay.setHours(hours, minutes, 0, 0);
    if (prevDay >= slot.startTime && prevDay < slot.endTime) {
      return prevDay;
    }

    // Fallback: return candidate as-is (validation will catch it)
    return candidate;
  };

  // Click outside to close modal
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // Check if click is on the backdrop (not the modal content)
      if (target.classList.contains('booking-modal-backdrop')) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Fetch settings when modal opens
  useEffect(() => {
    const fetchSettings = async () => {
      if (!starId) return;
      
      try {
        const starDoc = await getDoc(doc(db, 'users', starId));
        if (starDoc.exists()) {
          const starData = starDoc.data();
          const minDuration = starData.minCallDuration || 5;
          setSettings({
            minCallDuration: minDuration,
            maxCallDuration: starData.maxCallDuration || 30,
            pricePerMinute: starData.pricePerMinute || 1,
            minBidIncrement: starData.minBidIncrement || 5,
            allowBidding: starData.allowBidding !== undefined ? starData.allowBidding : true,
          });
          setBookingDuration(minDuration);
          setDurationInput(String(minDuration));
        }
      } catch (error) {
        console.error('Error fetching star settings:', error);
      }
    };

    if (isOpen) {
      fetchSettings();
    }
  }, [starId, isOpen]);

  // Fetch slots when date changes or modal opens
  useEffect(() => {
    const fetchSlots = async () => {
      if (!isOpen) return;
      
      setIsLoadingSlots(true);
      setError(null);
      
      try {
        // First fetch star details
        const starDoc = await getDoc(doc(db, 'users', starId));
        console.log('Star doc:', starDoc.data());
        const starData = starDoc.data();
        const starName = starData?.name || 'Star';

        // Get start of today and end of 30 days from now
        const today = startOfDay(new Date());
        const thirtyDaysFromNow = endOfDay(new Date());
        thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);

        console.log('Fetching slots from', today, 'to', thirtyDaysFromNow);

        // Query by startTime range (uses existing composite index on starId + startTime)
        // Include slots from 1 day ago to catch cross-midnight/active slots
        const oneDayAgo = new Date();
        oneDayAgo.setDate(oneDayAgo.getDate() - 1);

        const slotsQuery = query(
          collection(db, 'slots'),
          where('starId', '==', starId),
          where('startTime', '>=', oneDayAgo),
          where('startTime', '<=', thirtyDaysFromNow),
        );

        const querySnapshot = await getDocs(slotsQuery);
        const fetchedSlots = querySnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            starName: starName,
            startTime: data.startTime.toDate(),
            endTime: data.endTime.toDate(),
            bookings: (data.bookings || []).map((booking: any) => ({
              ...booking,
              startTime: booking.startTime.toDate(),
              endTime: booking.endTime.toDate(),
            })),
          } as TimeSlot;
        });

        // Filter out fully expired slots (endTime in the past)
        const now = new Date();
        const activeSlots = fetchedSlots.filter(slot => slot.endTime > now);
        console.log('Fetched slots:', fetchedSlots.length, 'Active:', activeSlots.length);
        setSlots(activeSlots);
        
        // When slots are fetched, find slots for the selected date
        if (selectedDate) {
          const selectedDaySlots = fetchedSlots.filter(slot => slotSpansDate(slot, selectedDate));
          
          if (selectedDaySlots.length > 0) {
            setSelectedSlot(selectedDaySlots[0]);
          } else {
            setSelectedSlot(null);
          }
        }

        console.log('Available slots by date:', slots.reduce((acc, slot) => {
          const date = format(slot.startTime, 'yyyy-MM-dd');
          if (!acc[date]) acc[date] = [];
          acc[date].push(slot);
          return acc;
        }, {} as Record<string, TimeSlot[]>));
      } catch (error) {
        console.error('Error loading slots:', error);
        setError('Failed to load available slots');
      } finally {
        setIsLoadingSlots(false);
      }
    };

    fetchSlots();
  }, [isOpen, starId]); // Remove selectedDate from dependencies

  // Add a separate useEffect to handle date selection
  useEffect(() => {
    if (slots.length > 0 && selectedDate) {
      const slotsForDay = slots.filter(slot => slotSpansDate(slot, selectedDate) && slot.endTime > new Date());
      setDaySlots(slotsForDay);

      if (slotsForDay.length > 0) {
        setSelectedSlot(slotsForDay[0]);
        const slotStart = slotsForDay[0].startTime;
        const now = new Date();
        const effectiveStart = slotStart > now ? slotStart : now;
        const h = effectiveStart.getHours().toString().padStart(2, '0');
        const m = effectiveStart.getMinutes().toString().padStart(2, '0');
        setTimeValue(`${h}:${m}`);
      } else {
        setSelectedSlot(null);
        setDaySlots([]);
      }
    }
  }, [selectedDate, slots]);

  // Auto-validate when slot, time, or duration changes
  useEffect(() => {
    if (selectedSlot && selectedDate && timeValue) {
      const [hours, minutes] = timeValue.split(':').map(Number);
      const start = buildBookingStart(hours, minutes, selectedSlot);
      handleTimeSelection(start, bookingDuration);
    }
  }, [selectedSlot, selectedDate, timeValue, bookingDuration]);

  const getAvailablePeriods = (slot: TimeSlot): Array<{start: Date; end: Date}> => {
    if (!slot) return [];

    const now = new Date();

    // If the entire slot has expired, return no available periods
    if (now >= slot.endTime) return [];

    const periods: Array<{start: Date; end: Date}> = [];
    let currentStart = new Date(Math.max(slot.startTime.getTime(), now.getTime()));

    // Sort bookings by start time
    const sortedBookings = [...(slot.bookings || [])].sort(
      (a, b) => a.startTime.getTime() - b.startTime.getTime()
    );

    // Add period before first booking
    if (sortedBookings.length > 0 && currentStart < sortedBookings[0].startTime) {
      periods.push({
        start: currentStart,
        end: sortedBookings[0].startTime
      });
    }

    // Add periods between bookings
    for (let i = 0; i < sortedBookings.length - 1; i++) {
      const currentEnd = sortedBookings[i].endTime;
      const nextStart = sortedBookings[i + 1].startTime;

      if (currentEnd < nextStart) {
        periods.push({
          start: currentEnd,
          end: nextStart
        });
      }
    }

    // Add period after last booking
    if (sortedBookings.length > 0) {
      const lastBooking = sortedBookings[sortedBookings.length - 1];
      if (lastBooking.endTime < slot.endTime) {
        periods.push({
          start: lastBooking.endTime,
          end: slot.endTime
        });
      }
    } else {
      // No bookings, entire slot is available
      periods.push({
        start: currentStart,
        end: slot.endTime
      });
    }

    // Clip periods to the selected calendar date boundaries
    // For cross-midnight slots: show only the portion that falls on the selected date
    if (selectedDate) {
      const dayStart = startOfDay(selectedDate);
      const dayEnd = endOfDay(selectedDate);

      return periods
        .map(period => ({
          start: period.start < dayStart ? dayStart : period.start,
          end: period.end > dayEnd ? dayEnd : period.end,
        }))
        .filter(period => period.start < period.end);
    }

    return periods;
  };

  const getOverlappingBookings = (start: Date, end: Date): Booking[] => {
    if (!selectedSlot) return [];
    
    // If bidding is not allowed, return empty array (no overlapping bookings can be outbid)
    if (!settings.allowBidding) return [];

    return selectedSlot.bookings.filter(booking => {
      const overlap = start < booking.endTime && end > booking.startTime;
      
      // Only include bookings that are more than 24 hours away
      const bookingTime = booking.startTime.getTime();
      const now = new Date().getTime();
      const is24HoursAway = (bookingTime - now) > (24 * 60 * 60 * 1000);
      
      return overlap && is24HoursAway;
    });
  };

  const validateTimeRange = (start: Date, end: Date, slot: TimeSlot): string | null => {
    const now = new Date();
    
    // Check if time is in the past
    if (start < now) {
      return "Cannot book time slots in the past";
    }

    // Check if booking is less than 24 hours away
    const timeUntilBooking = start.getTime() - now.getTime();
    // const twentyFourHoursInMs = 24 * 60 * 60 * 1000;
    const twentyFourHoursInMs = 10 * 60 * 1000;
    if (timeUntilBooking < twentyFourHoursInMs) {
      return "Cannot book calls less than 24 hours in advance";
    }

    // Check if time is within slot bounds
    if (start < slot.startTime || end > slot.endTime) {
      return "Selected time must be within the available slot";
    }

    // Check duration constraints
    const durationInMinutes = (end.getTime() - start.getTime()) / (1000 * 60);
    if (durationInMinutes < settings.minCallDuration) {
      return `Call duration must be at least ${settings.minCallDuration} minutes`;
    }
    if (durationInMinutes > settings.maxCallDuration) {
      return `Call duration cannot exceed ${settings.maxCallDuration} minutes`;
    }

    // For available periods, only check if there's no bidding involved
    const availablePeriods = getAvailablePeriods(slot);
    const fitsInAvailablePeriod = availablePeriods.some(period => 
      start >= period.start && end <= period.end
    );

    // Check if bidding is allowed
    if (settings.allowBidding) {
      // Allow bidding: check for biddable overlapping bookings
    const overlappingBookings = getOverlappingBookings(start, end);
    if (!fitsInAvailablePeriod && overlappingBookings.length === 0) {
      // Only return error if the time doesn't fit AND there are no biddable bookings
        return "Selected time conflicts with existing bookings";
      }
    } else {
      // Bidding not allowed: must fit in available period
      if (!fitsInAvailablePeriod) {
        return "Selected time conflicts with existing bookings. Bidding is not enabled.";
      }
    }

    return null;
  };

  const handleTimeSelection = (start: Date, duration: number) => {
    const end = new Date(start.getTime() + duration * 60000);
    
    if (!selectedSlot) return;

    const validationError = validateTimeRange(start, end, selectedSlot);
    if (validationError) {
      setError(validationError);
      setSelectedStartTime(null);
      setSelectedEndTime(null);
      setAffectedBookings([]);
      setBidAmount(0);
      return;
    }

    setError(null);
    setSelectedStartTime(start);
    setSelectedEndTime(end);
    
    const overlapping = getOverlappingBookings(start, end);
    setAffectedBookings(overlapping);
    
    if (overlapping.length > 0) {
      const highestBid = Math.max(...overlapping.map(b => b.currentBid));
      const minBidForOverlap = Math.ceil(highestBid * 1.1);
      setBidAmount(minBidForOverlap);
      setMinBid(minBidForOverlap);
    } else {
      setBidAmount(duration * (selectedSlot.basePrice/selectedSlot.duration));
    }
  };

  const handleDateChange = (value: Value) => {
    if (value instanceof Date) {
      setSelectedDate(value);
      setSelectedStartTime(null);
      setSelectedEndTime(null);
      setAffectedBookings([]);
      setBidAmount(0);
      setError(null);
      setEmailOnOutbid(true);
    }
  };

  const getBasePrice = (): number => {
    if (!selectedSlot) return 0;
    if (settings.allowBidding && affectedBookings.length > 0 && selectedStartTime && selectedEndTime) {
      // Calculate overlapping minutes
      const ratePerMinute = selectedSlot.basePrice / selectedSlot.duration;
      let overlappingMinutes = 0;
      for (const booking of affectedBookings) {
        const overlapStart = new Date(Math.max(selectedStartTime.getTime(), booking.startTime.getTime()));
        const overlapEnd = new Date(Math.min(selectedEndTime.getTime(), booking.endTime.getTime()));
        if (overlapStart < overlapEnd) {
          overlappingMinutes += (overlapEnd.getTime() - overlapStart.getTime()) / 60000;
        }
      }
      // Bid covers overlapping portion, regular rate for extra minutes
      const extraMinutes = bookingDuration - overlappingMinutes;
      return bidAmount + (extraMinutes > 0 ? extraMinutes * ratePerMinute : 0);
    }
    return bookingDuration * (selectedSlot.basePrice / selectedSlot.duration);
  };

  const getPlatformFee = (): number => {
    const base = getBasePrice();
    return Math.min(base * 0.05, 5);
  };

  const getTotalPrice = (): number => {
    return getBasePrice() + getPlatformFee();
  };

  const handleBooking = async () => {
    if (!selectedSlot || !selectedStartTime || !selectedEndTime || !user) {
      setError('Please select a valid time slot');
      return;
    }
    const totalPrice = getTotalPrice();
    if (user.credits < totalPrice) {
      setError('Insufficient credits to book this slot');
      return;
    }
    // Only check bid requirements if bidding is allowed
    if (settings.allowBidding && affectedBookings.length > 0) {
      const highestBid = Math.max(...affectedBookings.map(b => b.currentBid));
      const minBidRequired = Math.ceil(highestBid * 1.1);

      if (bidAmount < minBidRequired) {
        setError(`Minimum bid of $${minBidRequired} required`);
        return;
      }
    }

    setIsProcessingBooking(true);
    setError(null);

    try {
      const bookingId = crypto.randomUUID();
      const basePrice = getBasePrice();
      const platformFee = getPlatformFee();
      const bookingData: Booking = {
        id: bookingId,
        customerId: user.uid,
        fanId: user.uid,
        starId: starId,
        startTime: selectedStartTime,
        endTime: selectedEndTime,
        duration: bookingDuration,
        price: basePrice,
        currentBid: basePrice,
        platformFee: platformFee,
        status: 'locked',
        createdAt: new Date(),
        starName: selectedSlot.starName || 'Star',
        customerName: user.name || user.displayName || user.email || 'Fan',
        slotId: selectedSlot.id,
        emailOnOutbid: settings.allowBidding ? emailOnOutbid : false,
      };

      // If bidding is involved, use backend to handle refunds and complex operations
      if (settings.allowBidding && affectedBookings.length > 0) {
        const highestBid = Math.max(...affectedBookings.map(b => b.currentBid));
        const minBidRequired = Math.ceil(highestBid * 1.1);

        if (bidAmount < minBidRequired) {
          throw new Error(`Minimum bid of $${minBidRequired} required`);
        }

        // Call backend to process bidding (handles refunds, credit deduction, notifications)
        await axios.post(`${API_URL}/api/bookings/process-bidding`, {
          slotId: selectedSlot.id,
          newBookingId: bookingId,
          newBookingData: {
            ...bookingData,
            startTime: bookingData.startTime.toISOString(),
            endTime: bookingData.endTime.toISOString(),
            createdAt: bookingData.createdAt.toISOString(),
          },
          affectedBookings: affectedBookings.map(b => ({
            id: b.id,
            customerId: b.customerId,
            currentBid: b.currentBid,
            platformFee: b.platformFee || 0,
            startTime: b.startTime.toISOString(),
            endTime: b.endTime.toISOString(),
            emailOnOutbid: b.emailOnOutbid,
          })),
          currentUserId: user.uid,
          bidAmount: bidAmount,
          totalPrice: totalPrice,
          bookingDate: format(selectedStartTime, 'MMMM d, yyyy'),
          starName: selectedSlot.starName || 'Star',
        });

        // Update local user state after successful bid
        setUser({
          ...user,
          credits: user.credits - totalPrice
        });
      } else {
        // Regular booking — atomic transaction: deduct credits + create booking + update slot
        await runTransaction(db, async (transaction) => {
          // Read latest slot and user data inside transaction
          const slotRef = doc(db, 'slots', selectedSlot.id);
          const userRef = doc(db, 'users', user.uid);
          const slotSnap = await transaction.get(slotRef);
          const userSnap = await transaction.get(userRef);

          if (!slotSnap.exists()) {
            throw new Error('This slot no longer exists');
          }
          if (!userSnap.exists()) {
            throw new Error('User not found');
          }

          // Check credits at transaction time
          const currentCredits = userSnap.data().credits || 0;
          if (currentCredits < totalPrice) {
            throw new Error('Insufficient credits');
          }

          // Check for double-booking: re-read slot bookings and verify no conflict
          const latestBookings = (slotSnap.data().bookings || []).map((b: any) => ({
            startTime: b.startTime.toDate(),
            endTime: b.endTime.toDate(),
          }));
          const hasConflict = latestBookings.some((b: {startTime: Date; endTime: Date}) =>
            selectedStartTime < b.endTime && selectedEndTime > b.startTime
          );
          if (hasConflict) {
            throw new Error('This time slot was just booked by someone else. Please choose a different time.');
          }

          // All checks passed — perform atomic writes
          // 1. Deduct credits
          transaction.update(userRef, { credits: increment(-totalPrice) });

          // 2. Create booking document
          const bookingRef = doc(db, 'bookings', bookingId);
          transaction.set(bookingRef, bookingData);

          // 3. Add booking to slot's bookings array
          const existingBookings = slotSnap.data().bookings || [];
          transaction.update(slotRef, {
            bookings: [...existingBookings, bookingData]
          });

          // 4. Fan notification
          const notificationRef = doc(collection(db, `users/${user.uid}/notifications`));
          transaction.set(notificationRef, {
            userId: user.uid,
            type: 'booking_confirmed',
            title: 'Booking Confirmed',
            message: `Your booking with ${selectedSlot.starName} on ${format(selectedStartTime, 'MMM d, yyyy')} from ${format(selectedStartTime, 'h:mm a')} to ${format(selectedEndTime, 'h:mm a')} is confirmed.`,
            data: {
              bookingId: bookingId,
              amount: basePrice,
              platformFee: platformFee,
            },
            read: false,
            createdAt: new Date()
          });
        });

        // Update local user state after successful transaction
        setUser({
          ...user,
          credits: user.credits - totalPrice
        });

        // Notify the star via backend (has admin write access to other users)
        axios.post(`${API_URL}/api/bookings/notify`, {
          userId: starId,
          type: 'booking_confirmed',
          title: 'New Booking Received',
          message: `${user.displayName || 'A fan'} has booked a session with you on ${format(selectedStartTime, 'MMM d, yyyy')} from ${format(selectedStartTime, 'h:mm a')} to ${format(selectedEndTime, 'h:mm a')}.`,
          data: { bookingId, amount: basePrice },
        }).catch(err => console.error('Failed to notify star:', err));
      }

      // Send booking confirmation email for regular bookings (backend handles bidding emails)
      if (!settings.allowBidding || affectedBookings.length === 0) {
        try {
          if (user.email) {
            await sendBookingConfirmationEmail({
              recipientEmail: user.email,
              recipientName: user.displayName || user.email,
              starName: selectedSlot.starName || 'Star',
              bookingDate: format(selectedStartTime, 'MMMM d, yyyy'),
              bookingTime: `${format(selectedStartTime, 'h:mm a')} - ${format(selectedEndTime, 'h:mm a')}`,
              duration: bookingDuration,
              price: basePrice,
            });
          }
        } catch (emailError) {
          console.error('Error sending email:', emailError);
          // Don't fail the booking if email fails
        }
      }

      toast.success('Booking successful!');
      onClose();
    } catch (error) {
      console.error('Error booking slot:', error);
      toast.error('Failed to process booking. Please try again.');
      if (error instanceof Error) {
        setError(`Booking failed: ${error.message}`);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }

      await createNotification(
        user.uid,
        'payment_failed',
        'Payment Failed',
        'Your payment for the booking could not be processed. Please try again or contact support.',
        {
          amount: getTotalPrice()
        }
      );
    } finally {
      setIsProcessingBooking(false);
    }
  };

  const tileDisabled = ({date, view}: {date: Date; view: string}) => {
    if (view !== 'month') return false;

    // Disable past dates (before today)
    if (date < startOfDay(new Date())) return true;

    // Only enable dates that have at least one active (non-expired) slot
    return !slots.some(slot => slotSpansDate(slot, date) && slot.endTime > new Date());
  };

  // Handler for TimeSlotPicker
  const handleTimeChange = (value: string) => {
    setTimeValue(value);
    if (!selectedSlot) return;
    const [hours, minutes] = value.split(':').map(Number);
    const start = buildBookingStart(hours, minutes, selectedSlot);
    handleTimeSelection(start, bookingDuration);
  };

  return (
    <div className="booking-modal-backdrop fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-4 z-50 overflow-y-auto">
      <div className="relative bg-white rounded-lg p-4 sm:p-6 max-w-md w-full my-4 sm:my-8 shadow-xl">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Book a Call</h2>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 focus:outline-none"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        <Calendar
          onChange={handleDateChange}
          value={selectedDate}
          className="booking-calendar mb-4"
          tileDisabled={tileDisabled}
          tileClassName={({ date }) => {
            const hasSlot = slots.some(slot => slotSpansDate(slot, date));
            return hasSlot ? 'has-slots' : null;
          }}
        />

        {isLoadingSlots ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
          </div>
        ) : selectedSlot ? (
          <div className="space-y-4">
            {daySlots.length > 1 && (
              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {daySlots.length} slots available on this day — select one:
                </label>
                <select
                  value={selectedSlot.id}
                  onChange={(e) => {
                    const slot = daySlots.find(s => s.id === e.target.value);
                    if (slot) {
                      setSelectedSlot(slot);
                      const now = new Date();
                      const effectiveStart = slot.startTime > now ? slot.startTime : now;
                      const h = effectiveStart.getHours().toString().padStart(2, '0');
                      const m = effectiveStart.getMinutes().toString().padStart(2, '0');
                      setTimeValue(`${h}:${m}`);
                      setSelectedStartTime(null);
                      setSelectedEndTime(null);
                      setAffectedBookings([]);
                      setBidAmount(0);
                      setError(null);
                    }
                  }}
                  className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-gray-900 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  {daySlots.map((slot, idx) => (
                    <option key={slot.id} value={slot.id}>
                      Slot {idx + 1}: {format(slot.startTime, 'h:mm a')} - {format(slot.endTime, 'h:mm a')}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <h3 className="font-medium mb-2 text-gray-900">Available Times</h3>
              <div className="space-y-2">
                {getAvailablePeriods(selectedSlot).map((period, index) => (
                  <div 
                    key={index}
                    className="bg-green-100 border border-green-200 p-2 rounded text-green-800"
                  >
                    <p>{format(period.start, 'h:mm a')} - {format(period.end, 'h:mm a')}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <h3 className="font-medium mb-2 text-gray-900">Booked Times</h3>
              <div className="space-y-2">
                {[...selectedSlot.bookings].sort((a, b) => a.startTime.getTime() - b.startTime.getTime()).map((booking, index) => {
                  const is24HoursAway = (booking.startTime.getTime() - new Date().getTime()) > (24 * 60 * 60 * 1000);
                  const canBid = is24HoursAway && settings.allowBidding;
                  return (
                    <div 
                      key={index}
                      className={`p-2 rounded ${canBid ? 'bg-amber-100 border border-amber-200 text-amber-800' : 'bg-red-100 border border-red-200 text-red-800'}`}
                    >
                      <p>{format(booking.startTime, 'h:mm a')} - {format(booking.endTime, 'h:mm a')}</p>
                      {canBid && (
                        <p className="text-sm font-medium">Current bid: ${Number(booking.currentBid).toFixed(2)}</p>
                      )}
                      {is24HoursAway && !settings.allowBidding && (
                        <p className="text-xs text-red-600 mt-1">Bidding not available</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Time Selection */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select Start Time
              </label>
              <TimeSlotPicker
                value={timeValue}
                onChange={handleTimeChange}
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Duration (minutes)
              </label>
              <input
                type="number"
                min={settings.minCallDuration}
                max={settings.maxCallDuration}
                step="5"
                value={durationInput}
                onChange={(e) => {
                  const val = e.target.value;
                  setDurationInput(val);
                  const duration = parseInt(val);
                  if (isNaN(duration)) return;
                  setBookingDuration(duration);
                  if (!selectedSlot) return;
                  const [hours, minutes] = timeValue.split(':').map(Number);
                  const start = buildBookingStart(hours, minutes, selectedSlot);
                  handleTimeSelection(start, duration);
                }}
                onBlur={() => {
                  if (!durationInput || isNaN(parseInt(durationInput))) {
                    setBookingDuration(settings.minCallDuration);
                    setDurationInput(String(settings.minCallDuration));
                  }
                }}
                className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-gray-900 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              />
            </div>

            {/* Bid input section - only show when bidding on occupied slots */}
            {settings.allowBidding && affectedBookings.length > 0 && (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Your Bid
                </label>
                <input
                  type="number"
                  min={minBid || 0}
                  value={bidInput}
                  onChange={(e) => {
                    const val = e.target.value;
                    setBidInput(val);
                    const parsed = parseInt(val);
                    if (!isNaN(parsed)) setBidAmount(parsed);
                  }}
                  onBlur={() => {
                    if (!bidInput || isNaN(parseInt(bidInput))) {
                      setBidAmount(minBid || 0);
                      setBidInput(String(minBid || 0));
                    }
                  }}
                  className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-gray-900 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
                <p className="text-sm text-amber-600">
                  This time slot requires bidding (minimum: ${(minBid || 0).toFixed(2)})
                </p>
              </div>
            )}

            {/* Email notification opt-in - show for ALL bookings when bidding is enabled */}
            {settings.allowBidding && selectedStartTime && (
              <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <input
                  type="checkbox"
                  id="emailOnOutbid"
                  checked={emailOnOutbid}
                  onChange={(e) => setEmailOnOutbid(e.target.checked)}
                  className="mt-0.5 w-4 h-4 text-blue-600 bg-white border-gray-300 rounded focus:ring-blue-500 focus:ring-2 cursor-pointer"
                />
                <label htmlFor="emailOnOutbid" className="text-sm text-gray-700 cursor-pointer flex-1">
                  <span className="font-medium">Send me an email if I'm outbid</span>
                  <p className="text-xs text-gray-600 mt-1">
                    You'll be notified by email if someone places a higher bid on this time slot
                  </p>
                </label>
              </div>
            )}

            {selectedStartTime && selectedEndTime && (
              <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg mb-4">
                <h3 className="font-medium text-purple-800 mb-1">Booking Summary</h3>
                <p className="text-sm text-purple-700">
                  {format(selectedStartTime, 'MMMM d, yyyy')} at {format(selectedStartTime, 'h:mm a')} - {format(selectedEndTime, 'h:mm a')}
                </p>
                <p className="text-sm text-purple-700">
                  Duration: {bookingDuration} minutes
                </p>
                <p className="text-sm text-purple-700">
                  Session price: ${getBasePrice().toFixed(2)}
                </p>
                <p className="text-sm text-purple-700">
                  Platform fee: ${getPlatformFee().toFixed(2)}
                </p>
                <p className="text-sm font-semibold text-purple-800 pt-1 border-t border-purple-200 mt-1">
                  Total: ${getTotalPrice().toFixed(2)}
                </p>
              </div>
            )}

            <button
              onClick={handleBooking}
              disabled={!selectedStartTime || isProcessingBooking || !!error ||
                        !durationInput ||
                        user.credits < getTotalPrice()
                        || (settings.allowBidding && affectedBookings.length > 0 && (!bidInput || bidAmount < minBid))
                      }
              className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-4 py-2 rounded-lg font-medium disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
            >
              {isProcessingBooking ? 'Processing...' : (settings.allowBidding && affectedBookings.length > 0) ? 'Place Bid' : 'Book Call'}
            </button>
          </div>
        ) : (
          <p className="text-center text-gray-500 py-4">
            Select a date to view available slots
          </p>
        )}
      </div>
    </div>
  );
};

export default BookingModal;
